import { REFINE_ANALYTICS_EVENTS } from '@/types/RefineAnalyticsEvents';
import { errorHandler } from '@/Config/ErrorHandler';

interface RetryConfig {
	maxRetries?: number;
	delayMs?: number;
	endpoint: string;
	analytics: {
		trackRefineEvent: (eventName: REFINE_ANALYTICS_EVENTS, properties?: any) => void;
	};
}

const DEFAULT_CONFIG = {
	maxRetries: 2,
	delayMs: 1000,
} as const;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function fetchWithRetry<T>(
	url: string | URL,
	options: RequestInit = {},
	config: RetryConfig,
): Promise<T | null> {
	const { maxRetries, delayMs, endpoint, analytics } = { ...DEFAULT_CONFIG, ...config };
	let retryCount = 0;

	const urlString = url.toString();
	const hasScParam = urlString.includes('sc=');

	while (retryCount <= maxRetries) {
		try {
			const response = await fetch(urlString, options);

			if (response.ok) {
				if (retryCount > 0) {
					analytics.trackRefineEvent(REFINE_ANALYTICS_EVENTS.SUCCESSFUL_FETCH_AFTER_RETRY, {
						endpoint,
						retryCount,
					});
				}
				return await response.json();
			}

			throw new Error(`HTTP error! status: ${response.status}`);
		} catch (error: unknown) {
			const isLastAttempt = retryCount === maxRetries;
			if (isLastAttempt && hasScParam) {
				errorHandler('Error fetching data', {
					endpoint: config.endpoint,
					options,
					retryCount,
					isLastAttempt,
				});
			}

			if (isLastAttempt) {
				analytics.trackRefineEvent(REFINE_ANALYTICS_EVENTS.FETCH_FAILED_AFTER_RETRIES, {
					endpoint,
					error: error instanceof Error ? error.message : 'Unknown error',
				});
				return null;
			}

			await wait(delayMs);
			retryCount++;
		}
	}

	return null;
}
